import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Sheet, SheetContent } from "../ui/sheet";
import { Button } from "../ui/button";
import { TIER_META, fmtUsd, fmtRate, optimize, CASE_THUMBS, BRAND_BY_ID, imgFor, markLoaded } from "../../data/casesData";
import OpeningReel from "./OpeningReel";
import ItemResultPanel from "./ItemResultPanel";
import Confetti from "./Confetti";
import ItemImage from "./ItemImage";
import { useCredit } from "../../context/CreditContext";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { playRareWin, primeAudio } from "../../lib/sounds";
import { ShieldCheck, ChevronLeft, ChevronRight, Search, CheckCircle2, Loader2, X, Zap, Gift } from "lucide-react";

const rarityRank = { common: 0.4, uncommon: 0.6, rare: 1, epic: 1.5, legendary: 2.3, mythic: 3.2 };

export default function CaseDetailModal({ caseObj, list = [], onSelect, open, onClose }) {
  const { credit, openCase: openCaseApi, sellItem, startDeposit, demoOpen } = useCredit();
  const { user } = useAuth();
  const { toast } = useToast();
  const [phase, setPhase] = useState("idle"); // idle | loading | spinning | result
  const [winner, setWinner] = useState(null);
  const [spinKey, setSpinKey] = useState(0);
  const [shake, setShake] = useState(false);
  const [confettiKey, setConfettiKey] = useState(0);
  const [query, setQuery] = useState("");
  const [panelReady, setPanelReady] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fastSpin, setFastSpin] = useState(() => typeof window !== "undefined" && localStorage.getItem("fastSpin") === "1");
  const tokenRef = useRef(0);

  const toggleFast = () => setFastSpin((f) => { const n = !f; try { localStorage.setItem("fastSpin", n ? "1" : "0"); } catch (e) {} return n; });

  // Reset to browsing state whenever a different case is shown.
  useEffect(() => { setPhase("idle"); setWinner(null); setQuery(""); setPanelReady(false); setPreview(null); }, [caseObj?.id]);

  // Preload the thumbnail + every item image for this case; only reveal the
  // panel once they've all downloaded (with a min time so it never flickers).
  useEffect(() => {
    if (!caseObj) return;
    let cancelled = false;
    const urls = [optimize(CASE_THUMBS[caseObj.id], 480), ...caseObj.items.map((i) => imgFor(i.name))].filter(Boolean);
    let done = 0, minPassed = false, allDone = false;
    const ready = () => { if (minPassed && allDone && !cancelled) setPanelReady(true); };
    const tick = (u) => { markLoaded(u); done += 1; if (done >= urls.length) { allDone = true; ready(); } };
    const minTimer = setTimeout(() => { minPassed = true; ready(); }, 450);
    urls.forEach((u) => { const img = new Image(); img.onload = () => tick(u); img.onerror = () => tick(u); img.src = u; });
    const cap = setTimeout(() => !cancelled && setPanelReady(true), 12000);
    return () => { cancelled = true; clearTimeout(minTimer); clearTimeout(cap); };
    // eslint-disable-next-line
  }, [caseObj?.id]);

  const lastUpdated = useMemo(() => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}, ${p(d.getHours())}:${p(d.getMinutes())}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseObj?.id]);

  if (!caseObj) return null;

  const brand = BRAND_BY_ID[caseObj.id] || "";
  const idx = list.findIndex((c) => c.id === caseObj.id);
  const go = (dir) => {
    if (!list.length || idx < 0 || phase !== "idle") return;
    onSelect && onSelect(list[(idx + dir + list.length) % list.length]);
  };

  const sorted = [...caseObj.items].sort((a, b) => a.rate - b.rate);
  const items = query ? sorted.filter((i) => i.name.toLowerCase().includes(query.toLowerCase())) : sorted;

  // Make sure every image for this case is fully downloaded AND decoded
  // before the reel spins — so nothing loads mid-spin.
  const preloadForSpin = () => {
    const load = Promise.all(caseObj.items.map((i) => {
      const u = imgFor(i.name);
      const im = new Image();
      im.src = u;
      const p = im.decode ? im.decode() : new Promise((res) => { im.onload = im.onerror = res; });
      return p.then(() => markLoaded(u)).catch(() => markLoaded(u));
    }));
    // Generous safety cap so it can't hang forever on a dead asset.
    return Promise.race([load, new Promise((r) => setTimeout(r, 20000))]);
  };

  const openCase = async () => {
    primeAudio(); // unlock audio within the user gesture so ticks/win sounds play
    setPhase("loading");
    const token = ++tokenRef.current;
    let res;
    try {
      res = await openCaseApi(caseObj.id);
    } catch (e) {
      if (tokenRef.current === token) setPhase("idle");
      toast({ title: "Couldn't open case", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
      return;
    }
    if (tokenRef.current !== token) return;
    const w = { ...res.item, inventory_id: res.inventory_id };
    setWinner(w);
    const gate = fastSpin ? new Promise((r) => setTimeout(r, 80)) : Promise.all([preloadForSpin(), new Promise((r) => setTimeout(r, 750))]);
    gate.then(() => {
      if (tokenRef.current !== token) return;
      setPhase("spinning");
      setSpinKey((k) => k + 1);
    });
  };

  const demoSpin = async () => {
    primeAudio();
    setPhase("loading");
    const token = ++tokenRef.current;
    let item;
    try {
      item = await demoOpen(caseObj.id);
    } catch (e) {
      if (tokenRef.current === token) setPhase("idle");
      toast({ title: "Couldn't run demo", description: "Please try again.", variant: "destructive" });
      return;
    }
    if (tokenRef.current !== token) return;
    setWinner({ ...item, demo: true });
    const gate = fastSpin ? new Promise((r) => setTimeout(r, 80)) : Promise.all([preloadForSpin(), new Promise((r) => setTimeout(r, 750))]);
    gate.then(() => {
      if (tokenRef.current !== token) return;
      setPhase("spinning");
      setSpinKey((k) => k + 1);
    });
  };

  const onReelDone = () => {
    setPhase("result");
    setConfettiKey((k) => k + 1);
    playRareWin(rarityRank[winner.tier] || 1);
    if (["epic", "legendary", "mythic"].includes(winner.tier)) { setShake(true); setTimeout(() => setShake(false), 500); }
  };

  const finishResult = () => { setPhase("idle"); setWinner(null); };

  const wColor = winner ? TIER_META[winner.tier].color : caseObj.accent;

  return (
    <>
      {/* ── Side panel: browse items (closed while opening) ──── */}
      <Sheet open={open && phase === "idle"} onOpenChange={(o) => { if (!o && phase === "idle") onClose(); }}>
        <SheetContent side="right"
          className="w-full sm:!max-w-[560px] p-0 bg-[#0F0C16] text-white border-l border-[#2A2440] flex flex-col">

          {/* Full-panel loading swirl until thumbnail + all item images are ready */}
          <div className={`absolute inset-0 z-40 flex items-center justify-center bg-[#0F0C16] transition-opacity duration-300 ${panelReady ? "opacity-0 pointer-events-none" : "opacity-100"}`}
            data-testid="case-panel-loader">
            <Loader2 className="h-11 w-11 animate-spin text-[#8A90A0]" strokeWidth={2.5} />
          </div>

          {/* Expanded item preview */}
          {preview && (
            <div data-testid="item-preview" onClick={() => setPreview(null)}
              className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-[#19142A] p-6 animate-in fade-in duration-200">
              <button data-testid="item-preview-close" onClick={(e) => { e.stopPropagation(); setPreview(null); }}
                className="absolute right-4 top-4 h-9 w-9 rounded-full flex items-center justify-center text-[#6B7280] hover:bg-black/5 transition-colors">
                <X className="h-5 w-5" strokeWidth={2.5} />
              </button>
              <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-center max-w-sm w-full animate-in zoom-in-95 duration-200">
                <span className="text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full mb-4"
                  style={{ background: TIER_META[preview.tier].color + "22", color: TIER_META[preview.tier].color }}>
                  {TIER_META[preview.tier].label} · {fmtRate(preview.rate)}
                </span>
                <div className="relative h-56 w-56 flex items-center justify-center mb-5">
                  <span className="absolute inset-6 rounded-full blur-3xl opacity-40" style={{ background: `radial-gradient(circle, ${TIER_META[preview.tier].color} 0%, transparent 70%)` }} />
                  <div className="relative" style={{ filter: `drop-shadow(0 8px 18px ${TIER_META[preview.tier].color}66)` }}>
                    <ItemImage item={preview} imgClass="h-52 w-52" iconClass="h-20 w-20" color={TIER_META[preview.tier].color} eager />
                  </div>
                </div>
                <div className="text-center font-heading text-xl font-bold text-white leading-tight">{preview.name}</div>
                <div className="text-sm text-[#8A90A0] mt-0.5">{brand}</div>
                <div className="mt-3 font-cases text-lg bg-[#19142A] text-white px-4 py-1.5">{fmtUsd(preview.value)}</div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="relative bg-[#19142A] px-6 pt-8 pb-3 border-b border-[#2A2440]">
            <button data-testid="case-close-btn" onClick={onClose}
              className="absolute right-3 sm:left-3 sm:right-auto top-3 z-10 h-9 w-9 rounded-full flex items-center justify-center text-[#6B7280] hover:bg-black/5 hover:text-white transition-colors">
              <X className="h-5 w-5" strokeWidth={2.5} />
            </button>
            <button data-testid="case-prev-btn" onClick={() => go(-1)}
              className="absolute left-3 top-[64px] h-10 w-10 rounded-full flex items-center justify-center text-white hover:bg-black/5 transition-colors">
              <ChevronLeft className="h-7 w-7" strokeWidth={3} />
            </button>
            <button data-testid="case-next-btn" onClick={() => go(1)}
              className="absolute right-3 top-[64px] h-10 w-10 rounded-full flex items-center justify-center text-white hover:bg-black/5 transition-colors">
              <ChevronRight className="h-7 w-7" strokeWidth={3} />
            </button>

            <div className="relative mx-auto h-24 w-24 flex items-center justify-center">
              <img key={caseObj.id} src={optimize(CASE_THUMBS[caseObj.id], 480)} alt={caseObj.name}
                className="h-24 w-24 object-contain drop-shadow-[0_10px_20px_rgba(16,20,36,0.22)]" loading="eager" decoding="async" />
            </div>
            <h2 data-testid="case-detail-name" className="mt-2 text-center font-cases text-xl uppercase tracking-tight">{caseObj.name}</h2>
            <div className="mt-1.5 flex items-center justify-center gap-2">
              <span className="text-sm text-[#8A90A0]">{brand}</span>
              <span className="font-cases text-sm bg-[#19142A] text-white px-2.5 py-0.5">{fmtUsd(caseObj.price)}</span>
            </div>
          </div>

          {/* Items in this box */}
          <div className="flex items-center justify-between px-6 pt-3 pb-2">
            <span className="text-xs font-bold uppercase tracking-wide text-[#6B7280]">Items in this box</span>
            <span className="text-xs font-bold text-[#9AA0AE]">{caseObj.items.length}</span>
          </div>
          <div className="px-6 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9AA0AE]" />
              <input data-testid="case-items-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search items…"
                className="w-full border border-[#2A2440] bg-[#19142A] pl-9 pr-3 py-2 text-sm outline-none focus:border-[#3A2F5C]" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-6">
            <div className="grid grid-cols-3 gap-2">
              {items.map((it, i) => {
                const color = TIER_META[it.tier].color;
                return (
                  <div key={it.name + i} data-testid="case-item-card" onClick={() => setPreview(it)} role="button" tabIndex={0}
                    className="relative rounded-lg border border-[#2A2440] bg-[#19142A] p-1.5 flex flex-col shadow-[0_1px_2px_rgba(16,20,36,0.05)] cursor-pointer hover:-translate-y-0.5 hover:shadow-md hover:border-[#3A2F5C] transition-[transform,box-shadow,border-color] duration-200">
                    <span className="absolute top-1.5 left-1.5 z-10 text-[10px] font-bold bg-black/50 text-[#C4B5FD] border border-white/10 rounded px-1.5 py-0.5 shadow-sm">{fmtRate(it.rate)}</span>
                    <span className="absolute top-1.5 right-1.5 z-10 h-4 w-4 rounded-[3px]" style={{ background: color }} />
                    <div className="mt-6 mb-1.5 flex items-center justify-center">
                      <div className="h-[70px] w-[70px] rounded-md overflow-hidden flex items-center justify-center" style={{ background: color + "12" }}>
                        <ItemImage item={it} imgClass="h-[70px] w-[70px]" iconClass="h-7 w-7" color={color} />
                      </div>
                    </div>
                    <div className="text-[10px] font-bold text-white leading-tight line-clamp-2 min-h-[2.2em]">{it.name}</div>
                    <div className="mt-1 flex items-center justify-between gap-1">
                      <span className="text-[10px] font-bold bg-[#10B981]/15 text-[#34D399] rounded px-1.5 py-0.5">{fmtUsd(it.value)}</span>
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" style={{ color }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {!items.length && <div className="text-center text-sm text-[#9AA0AE] py-8">No items match “{query}”.</div>}
          </div>

          {/* Footer */}
          <div className="shrink-0 bg-[#19142A] border-t border-[#2A2440] p-4 flex flex-col items-center gap-2">
            <button data-testid="fast-spin-toggle" onClick={toggleFast} aria-pressed={fastSpin}
              className={`w-full h-9 flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wide border transition-colors ${fastSpin ? "border-[#8B5CF6] bg-[#8B5CF6]/10 text-[#7C3AED]" : "border-[#2A2440] bg-[#19142A] text-[#9AA0AE] hover:text-white"}`}>
              <Zap className="h-3.5 w-3.5" fill={fastSpin ? "currentColor" : "none"} />
              Fast Spin {fastSpin ? "On" : "Off"}
            </button>
            <Button data-testid="demo-spin-btn" onClick={demoSpin}
              className="w-full h-10 text-sm font-bold font-heading rounded-lg border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 text-[#7C3AED] hover:bg-[#8B5CF6]/20 gap-2">
              <Gift className="h-4 w-4" /> Free Demo Spin
            </Button>
            {!user ? (
              <Button data-testid="signin-to-play-btn"
                onClick={() => toast({ title: "Sign in to play", description: "Create an account or sign in from the top-right to deposit and open cases." })}
                className="w-full h-12 text-base font-bold font-heading text-white border-0 hover:opacity-95 bg-gradient-to-r from-[#22D3EE] to-[#8B5CF6]">
                Sign in to Play
              </Button>
            ) : credit >= caseObj.price ? (
              <Button data-testid="open-case-btn" onClick={openCase}
                className="w-full h-12 text-base font-bold font-heading text-white border-0 hover:opacity-95" style={{ background: caseObj.accent }}>
                Open Case — {fmtUsd(caseObj.price)}
              </Button>
            ) : (
              <Button data-testid="deposit-to-play-btn"
                onClick={async () => { try { const url = await startDeposit(Math.max(5, Math.ceil(caseObj.price))); window.location.href = url; } catch (e) { toast({ title: "Couldn't start deposit", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" }); } }}
                className="w-full h-12 text-base font-bold font-heading text-white border-0 hover:opacity-95 bg-gradient-to-r from-[#22D3EE] to-[#8B5CF6]">
                Deposit to Play
              </Button>
            )}
            <div className="flex items-center gap-1 text-[11px] text-[#9AA0AE]">
              <ShieldCheck className="h-3 w-3 text-[#16A34A]" /> Provably fair · balance {fmtUsd(credit)}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Full-screen opener: loading → spin → result ──────── */}
      {open && phase !== "idle" && createPortal(
        <div data-testid="fullscreen-opener"
          style={{ pointerEvents: "auto", background: "#090C14" }}
          className="fixed inset-0 z-[120] flex flex-col items-center justify-center backdrop-blur-lg p-4 animate-in fade-in duration-300">

          <Confetti fireKey={phase === "result" ? confettiKey : 0} color={wColor} intensity={winner ? rarityRank[winner.tier] : 1} />
          {phase === "result" && ["epic", "legendary", "mythic"].includes(winner?.tier) && (
            <div className="flash absolute inset-0 z-0 pointer-events-none" style={{ background: wColor }} />
          )}

          {/* case identity */}
          <div className="relative z-10 flex flex-col items-center mb-6">
            <img src={optimize(CASE_THUMBS[caseObj.id], 320)} alt={caseObj.name} className="h-16 w-16 object-contain drop-shadow-[0_6px_16px_rgba(0,0,0,0.5)]" />
            <div className="mt-2 font-cases text-xl uppercase tracking-tight text-white">{caseObj.name}</div>
          </div>

          {phase === "loading" && (
            <div className="relative z-10 flex flex-col items-center gap-4 animate-in fade-in duration-200">
              <Loader2 className="h-12 w-12 animate-spin" style={{ color: caseObj.accent }} strokeWidth={2.5} />
              <div className="text-sm uppercase tracking-[0.3em] text-white/70">Preparing your case</div>
            </div>
          )}

          {phase === "spinning" && (
            <div className="relative z-10 w-full animate-in zoom-in-95 fade-in duration-300">
              <OpeningReel caseObj={caseObj} winner={winner} spinKey={spinKey} onDone={onReelDone} fast={fastSpin} />
            </div>
          )}

          {phase === "result" && winner && (
            <div className={`relative z-10 w-full max-w-lg bg-[#19142A] dark:bg-[#141021] rounded-lg p-6 sm:p-8 shadow-2xl animate-in zoom-in-95 fade-in duration-300 ${shake ? "shake" : ""}`}>
              <button data-testid="opener-close-btn" onClick={finishResult}
                className="absolute right-3 top-3 h-8 w-8 rounded-full flex items-center justify-center text-[#9AA0AE] hover:bg-black/5 transition-colors">
                <X className="h-5 w-5" />
              </button>
              {winner.demo ? (
                <div className="text-center" data-testid="demo-result-panel">
                  <div className="inline-block px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider mb-3"
                    style={{ color: wColor, background: `${wColor}1f`, border: `1px solid ${wColor}55` }}>Demo spin</div>
                  <div className="mx-auto h-28 w-28 rounded-xl overflow-hidden flex items-center justify-center bg-[#19142A] dark:bg-[#151B27] border border-[#2A2440] dark:border-[#2A2440] mb-3">
                    <ItemImage item={winner} imgClass="h-28 w-28" iconClass="h-14 w-14" color={wColor} />
                  </div>
                  <div className="font-heading text-2xl font-extrabold text-white dark:text-white">{winner.name}</div>
                  <div className="mt-1 font-grotesk font-bold text-[#16A34A]">{fmtUsd(winner.value)}</div>
                  <p className="text-sm text-[#8A90A0] mt-3">This was a free demo — no money spent, nothing added. Open for real to keep what you win!</p>
                  <div className="flex flex-col sm:flex-row gap-2 mt-4">
                    <Button data-testid="demo-spin-again-btn" onClick={demoSpin} variant="outline"
                      className="w-full sm:flex-1 h-12 rounded-lg border-[#8B5CF6]/40 bg-[#8B5CF6]/10 text-[#7C3AED] hover:bg-[#8B5CF6]/20 gap-2 font-bold">
                      <Gift className="h-4 w-4" /> Demo again
                    </Button>
                    <Button data-testid="demo-open-real-btn" onClick={() => { finishResult(); if (user && credit >= caseObj.price) openCase(); }}
                      className="w-full sm:flex-1 h-12 text-base font-bold font-heading text-white border-0" style={{ background: caseObj.accent }}>
                      {user && credit >= caseObj.price ? `Open for real — ${fmtUsd(caseObj.price)}` : "Close"}
                    </Button>
                  </div>
                </div>
              ) : (
                <ItemResultPanel item={winner}
                  price={caseObj.price}
                  accent={caseObj.accent}
                  canSpinAgain={!!user && credit >= caseObj.price}
                  onSpinAgain={() => { if (user && credit >= caseObj.price) openCase(); }}
                  onKeep={() => { toast({ title: "Kept — added to your inventory", description: winner.name }); finishResult(); }}
                  onSell={async () => { try { const payout = await sellItem(winner.inventory_id); toast({ title: "Sold", description: `+${fmtUsd(payout)} credit` }); } catch (e) { toast({ title: "Couldn't sell", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" }); } finishResult(); }}
                  onShip={() => { toast({ title: "Saved to your inventory", description: "Request shipping or cash-out anytime from Wallet → Withdraw." }); finishResult(); }} />
              )}
            </div>
          )}
        </div>, document.body)}
    </>
  );
}
